// pages/Agents/Agents.js
import React, { useState, useEffect, useCallback } from "react";
import axiosInstance from "../../utils/axiosInstance";
import { hasPermission } from "../../utils/permissionUtils";
import { Link, useParams } from "react-router-dom";
import {
  Paper,
  Divider,
  Box,
  Typography,
  Alert,
  Grid,
  Card,
  CardContent,
  CardHeader,
  Switch,
  Button,
  CircularProgress,
} from "@mui/material";
import { styled } from "@mui/material/styles";

const StatusText = styled(Typography)(({ theme, configured }) => ({
  color: configured ? theme.palette.success.main : theme.palette.warning.main,
}));

export default function Agents() {
  const [agents, setAgents] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const { chatbotId: paramsChatbotId } = useParams();

  const [canView, setCanView] = useState(false);
  const [canModify, setCanModify] = useState(false);
  const [canDelete, setCanDelete] = useState(false);
  const [checkingPermissions, setCheckingPermissions] = useState(true);

  useEffect(() => {
    const checkPermissions = async () => {
      setCheckingPermissions(true);
      try {
        const viewPerm = await hasPermission("agents", "view");
        const modifyPerm = await hasPermission("agents", "add|edit");
        const deletePerm = await hasPermission("agents", "delete");

        setCanView(viewPerm);
        setCanModify(modifyPerm);
        setCanDelete(deletePerm);
      } catch (error) {
        console.error("Error checking permissions:", error);
        setCanView(false);
        setCanModify(false);
        setCanDelete(false);
      } finally {
        setCheckingPermissions(false);
      }
    };

    checkPermissions();
  }, []);

  const fetchAgentStatus = useCallback(async () => {
    try {
      const params = paramsChatbotId ? { chatbot_id: paramsChatbotId } : {};
      const response = await axiosInstance.get(`/api/agents/status`, {
        params,
      });
      setAgents(response.data.agents);
    } catch (err) {
      setError("Failed to load agent statuses.");
    } finally {
      setLoading(false);
    }
  }, [paramsChatbotId]);

  useEffect(() => {
    document.title = "Manage Agents | Klonit";
    if (canView) {
      fetchAgentStatus();
    }
  }, [fetchAgentStatus, canView]);

  const handleToggle = async (agentName, newStatus) => {
    if (!canModify) return;

    setAgents((prev) => ({
      ...prev,
      [agentName]: { ...prev[agentName], is_active: newStatus },
    }));

    try {
      const params = paramsChatbotId ? { chatbot_id: paramsChatbotId } : {};
      await axiosInstance.post(
        `/api/agents/status`,
        { agent_name: agentName, is_active: newStatus },
        { params }
      );
    } catch (err) {
      setError(`Failed to update ${agentName} status.`);
      setAgents((prev) => ({
        ...prev,
        [agentName]: { ...prev[agentName], is_active: !newStatus },
      }));
    }
  };

  if (checkingPermissions) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", p: 5 }}>
        <Typography>Checking permissions...</Typography>
      </Box>
    );
  }

  if (!canView) {
    return (
      <Box sx={{ p: 4 }}>
        <Alert severity="error" sx={{ mt: 6 }}>
          <strong>Access Denied</strong>
          <br />
          You don't have permission to view Agents. Please contact your
          administrator.
        </Alert>
      </Box>
    );
  }

  if (loading)
    return (
      <Box sx={{ display: "flex", justifyContent: "center", p: 4 }}>
        <CircularProgress />
      </Box>
    );

  if (error)
    return (
      <Alert severity="error" sx={{ my: 2 }}>
        {error}
      </Alert>
    );

  const renderAgentCard = (agentName, config) => {
    const { is_active, is_configured } = config;

    let link, description;
    switch (agentName) {
      case "ROUTER":
        link = "router";
        description =
          "Configures the main routing agent that directs queries to the correct specialist.";
        break;
      case "GOOGLE_CALENDAR":
        link = "google-calendar";
        description = "Allows the AI to manage your Google Calendar events.";
        break;
      case "GMAIL_EMAIL":
        link = "gmail-email";
        description =
          "Allows the AI to manage your Gmail emails - read, search, count, and reply.";
        break;
      case "OUTLOOK_EMAIL":
        link = "outlook-email";
        description =
          "Allows the AI to manage your Outlook emails - read, search, count, and reply.";
        break;
      case "MYSQL":
        link = "mysql";
        description = "Allows the AI to query your MySQL database.";
        break;
      default:
        link = "#";
        description = "Unknown agent.";
    }

    return (

        
      <Grid item xs={12} md={6} key={agentName}>
        <Card
          variant="outlined"
          sx={{
            borderRadius: 3,
            boxShadow: 2,
            height: "100%",
            display: "flex",
            flexDirection: "column",
            justifyContent: "space-between",
          }}
        >
          <CardHeader
            title={`${agentName.replace("_", " ")} Agent`}
            subheader={
              <StatusText variant="subtitle2" configured={is_configured}>
                {is_configured ? "Configured" : "Not Configured"}
              </StatusText>
            }
            action={
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <Switch
                  checked={is_active}
                  onChange={(e) => handleToggle(agentName, e.target.checked)}
                  disabled={
                    (!is_configured && agentName !== "ROUTER") || !canModify
                  }
                />
                <Typography variant="body2">
                  {is_active ? "Active" : "Inactive"}
                </Typography>
              </Box>
            }
          />
          <CardContent>
            <Typography variant="body2" sx={{ mb: 2 }}>
              {description}
            </Typography>
            {canModify ? (
              <Button
                component={Link}
                to={link}
                variant="outlined"
                color="primary"
              >
                {is_configured ? "Manage Configuration" : "Configure"}
              </Button>
            ) : (
              <Typography variant="body2" color="text.secondary">
                Contact administrator to configure{" "}
                {agentName.replace("_", " ")} Agent
              </Typography>
            )}
          </CardContent>
        </Card>
      </Grid>
    );
  };

  return (
    <Paper sx={{ p: 3, mt: 10, mx: 'auto', maxWidth: '1200px', bgcolor: 'background.paper' }}>
        <Typography variant="h4" component="h1" sx={{ mb: 4, fontWeight: 700 }} color="textPrimary">
                Manage Your AI Agents
        </Typography>
        <Divider sx={{ mb: 3 }} />
        <Box sx={{ p: 3 }}>
            {!canModify && (
                <Alert severity="info" sx={{ mb: 3 }}>
                You have view-only access. Contact your administrator to make changes.
                </Alert>
            )}
            <Grid container spacing={3}>
                {agents &&
                Object.entries(agents)
                    .filter(([name]) => name !== "ROUTER")
                    .sort(([a], [b]) => a.localeCompare(b))
                    .map(([name, config]) => renderAgentCard(name, config))}
            </Grid>
        </Box>
    </Paper>
  );
}
